import { motion } from 'framer-motion';
import { LeafIcon, ThermometerIcon, DropletsIcon, WindIcon, FlaskConicalIcon } from 'lucide-react';
import { useTelemetry } from '../../hooks/useTelemetry';
import { useEditorZones } from '../../hooks/useEditorZones';
import { useTranslation } from 'react-i18next';
import { EnergyChart } from './EnergyChart';
import { MonthlyEnergyWidget } from './MonthlyEnergyWidget';
import { TopOutletWidget } from './TopOutletWidget';
import { OutdoorEnvironmentWidget } from './OutdoorEnvironmentWidget';

interface AllSpacesViewProps {
  apartmentAssetId: string | null;
  homeName: string | null;
  seoSensors?: any[];
}

export function AllSpacesView({ apartmentAssetId, homeName, seoSensors = [] }: AllSpacesViewProps) {
  const { t } = useTranslation();
  
  // Fetch zone hierarchy to identify primary zone and its sensors
  const { zones, primaryZoneId } = useEditorZones(apartmentAssetId);

  // Identify the primary zone and the environment sensor within it
  const primaryZone = zones.find(z => z.id === primaryZoneId) || zones.find(z => z.name === '거실') || zones[0];
  const primaryEnvSensorId = primaryZone?.sensors?.find(s => s.type === 'SES')?.id;

  // Real-time telemetry for primary zone's SES sensor (or fallback to apartment asset)
  const targetId = primaryEnvSensorId || apartmentAssetId;
  const targetType = primaryEnvSensorId ? 'DEVICE' : 'ASSET';

  const { data: telemetry } = useTelemetry(
    targetId,
    ['temperature', 'humidity', 'dust', 'TVOCs', 'CO2'],
    targetType
  );

  // Extract values or fallback
  const temp = telemetry['temperature']?.[0]?.value ? Number(telemetry['temperature'][0].value) : 0;
  const humidity = telemetry['humidity']?.[0]?.value ? Number(telemetry['humidity'][0].value) : 0;
  const aqi = telemetry['dust']?.[0]?.value ? Number(telemetry['dust'][0].value) : 0;
  const tvoc = telemetry['TVOCs']?.[0]?.value ? Number(telemetry['TVOCs'][0].value) : 0;
  const co2 = telemetry['CO2']?.[0]?.value ? Number(telemetry['CO2'][0].value) : 0;

  // AQI status
  const aqiStatus = aqi === 0 ? '-' : aqi <= 50 ? t('sidebar.aqi_good') : aqi <= 100 ? t('sidebar.aqi_fair') : t('sidebar.aqi_bad');
  const aqiColor = aqi === 0 ? 'text-slate-500' : aqi <= 50 ? 'text-emerald-400' : aqi <= 100 ? 'text-amber-400' : 'text-red-400';

  // Humidity status
  const humidityOk = humidity >= 30 && humidity <= 60;
  const humidityColor = humidity === 0 ? 'text-slate-500' : humidityOk ? 'text-cyan-400' : 'text-amber-400';

  // TVOC status
  const tvocStatus = tvoc === 0 ? '-' : tvoc < 200 ? t('sidebar.tvoc_good') : tvoc < 400 ? t('sidebar.tvoc_fair') : t('sidebar.tvoc_bad');
  const tvocColor = tvoc === 0 ? 'text-slate-500' : tvoc < 200 ? 'text-emerald-400' : tvoc < 400 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="w-full h-full overflow-y-auto pb-20 px-4 pt-4 hide-scrollbar flex flex-col gap-4">
      {/* 0. Outdoor Environment Section */}
      <OutdoorEnvironmentWidget assetId={apartmentAssetId} />

      {/* 1. Indoor Environment Section */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between mb-1 px-1">
          <div className="flex items-center gap-2">
            <LeafIcon className="w-4 h-4 text-emerald-400" />
            <span className="text-[1.05rem] font-medium tracking-tight text-[var(--mobile-text-primary)]">
              {t('sidebar.indoor_env')}
            </span>
          </div>
          {primaryZone && (
            <span className="text-[0.7rem] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-medium">
              {primaryZone.name} 기준
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          {/* Avg Temp */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="p-3 rounded-2xl border bg-[var(--mobile-bg-card)] border-[var(--mobile-border)] shadow-sm"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-1.5">
                <ThermometerIcon className="w-4 h-4 text-amber-400" />
                <span className="text-[0.8rem] uppercase font-bold tracking-wider text-[var(--mobile-text-muted)]">
                  {t('sidebar.indoor_temp')}
                </span>
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-mono font-bold glow-text-amber text-[var(--mobile-text-primary)]">
                {temp === 0 ? '-' : temp.toFixed(1)}°C
              </span>
              <span className="text-[0.65rem] font-mono mt-1 text-[var(--mobile-text-muted)]">
                {t('sidebar.temp_optimal_range')}
              </span>
            </div>
          </motion.div>

          {/* Humidity */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-3 rounded-2xl border bg-[var(--mobile-bg-card)] border-[var(--mobile-border)] shadow-sm"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-1.5">
                <DropletsIcon className={`w-4 h-4 ${humidityColor}`} />
                <span className="text-[0.8rem] uppercase font-bold tracking-wider text-[var(--mobile-text-muted)]">
                  {t('sidebar.indoor_humidity')}
                </span>
              </div>
            </div>
            <div className="flex flex-col">
              <span className={`text-xl font-mono font-bold ${humidityColor}`}>
                {humidity === 0 ? '-' : `${humidity.toFixed(1)}%`}
              </span>
              <span className="text-[0.65rem] font-mono mt-1 text-[var(--mobile-text-muted)]">
                {humidity === 0 ? '-' : humidityOk ? t('sidebar.humidity_optimal') : t('sidebar.humidity_adjusting')}
              </span>
            </div>
          </motion.div>

          {/* Air Quality */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="p-3 rounded-2xl border bg-[var(--mobile-bg-card)] border-[var(--mobile-border)] shadow-sm"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-1.5">
                <WindIcon className={`w-4 h-4 ${aqiColor}`} />
                <span className="text-[0.8rem] uppercase font-bold tracking-wider text-[var(--mobile-text-muted)]">
                  {t('sidebar.air_quality')}
                </span>
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex items-end gap-1.5">
                <span className={`text-xl font-mono font-bold ${aqiColor}`}>
                  {aqi === 0 ? '-' : aqi.toFixed(0)}
                </span>
                <span className={`text-[0.65rem] font-bold pb-0.5 ${aqiColor}`}>
                  {aqiStatus}
                </span>
              </div>
              <span className="text-[0.65rem] font-mono mt-1 text-[var(--mobile-text-muted)]">
                PM2.5: {aqi === 0 ? '-' : aqi.toFixed(0)} | CO2: {co2 === 0 ? '-' : co2.toFixed(0)}
              </span>
            </div>
          </motion.div>

          {/* TVOC */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="p-3 rounded-2xl border bg-[var(--mobile-bg-card)] border-[var(--mobile-border)] shadow-sm"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-1.5">
                <FlaskConicalIcon className={`w-4 h-4 ${tvocColor}`} />
                <span className="text-[0.8rem] uppercase font-bold tracking-wider text-[var(--mobile-text-muted)]">TVOC</span>
              </div>
            </div>
            <div className="flex flex-col">
               <div className="flex items-end gap-1.5">
                <span className={`text-xl font-mono font-bold ${tvocColor}`}>
                  {tvoc === 0 ? '-' : tvoc.toFixed(0) }
                </span>
                <span className={`text-[0.65rem] font-bold pb-0.5 ${tvocColor}`}>
                  {tvocStatus}
                </span>
              </div>
              <span className="text-[0.65rem] font-mono mt-1 text-[var(--mobile-text-muted)]">
                {t('sidebar.tvoc_desc')}
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* 2. Today's Electric Usage */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="min-h-[175px] w-full"
      >
        <EnergyChart currentAssetId={apartmentAssetId} />
      </motion.div>

      {/* 3. Monthly Electric Usage */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="min-h-[360px] w-full"
      >
        <MonthlyEnergyWidget currentAssetId={apartmentAssetId} />
      </motion.div>

      {/* 4. Top Outlets */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="min-h-[240px] w-full"
      >
        <TopOutletWidget seoSensors={seoSensors} />
      </motion.div>

    </div>
  );
}
